import { AccessLevel } from "@/api/williams/users";
import { ConfirmationDialog } from "@/components/dialogs";
import { faCalendar } from "@fortawesome/free-regular-svg-icons";
import { faRefresh, faSearch, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { getFullPath, routes } from "../../routes";
import { IconButton } from "../../components";
import { ScopeOfWork, ScopeOfWorkStatus } from "@/api/williams/scopesOfWork";
import { SOWFormWelderRow } from "./SOWFormWelderRow";
import { useFormContext } from "react-hook-form";
import { useModalDialog } from "@/hooks";
import { useNavigate } from "react-router";
import { usePermissions } from "../../users/hooks";
import { useState } from "react";
import { useWelderAdvisories } from "../hooks";
import { Welder } from "@/api/williams/welders";
import { WelderSelectionDialog } from "../../welders/dialogs";

export function SOWFormWelders({ data }: { data: Readonly<ScopeOfWork> }) {
  const [selected, setSelected] = useState<Welder[]>([]);

  const navigate = useNavigate();
  const form = useFormContext<ScopeOfWork>();
  const dialog = useModalDialog();
  const permissions = usePermissions();
  const update = useWelderAdvisories();

  function handleSelectWelders() {
    if (data.status < ScopeOfWorkStatus.Active) {
      // Trigger extended form validation.
      form.setValue("status", ScopeOfWorkStatus.Active);
    }

    return form.handleSubmit(selectWelders)();
  }

  function selectWelders() {
    dialog.show(WelderSelectionDialog, {
      initialWelders: data.welders,
      onAccept: (selectedWelders) => {
        form.setValue("welders", selectedWelders, { shouldDirty: true });
      },
    });
  }

  function handleAddRenewal() {
    navigate(getFullPath(routes.welder.renewal), {
      state: {
        welders: selected,
      },
    });
  }

  function handleCreateTestEvent() {
    navigate(getFullPath(routes.testEvent.create), {
      state: {
        welders: selected,
        project: data.project,
      },
    });
  }

  function handleRefresh() {
    dialog.show(ConfirmationDialog, {
      caption: "Update Results",
      message: "Are you sure you want to update the results?",
      onAccept: () => {
        update.mutate(data, {
          onSuccess: (scope) => {
            form.reset(scope, { keepDefaultValues: true });
          },
        });
      },
    });
  }

  function handleRemoveWelder(welder: Welder) {
    dialog.show(ConfirmationDialog, {
      caption: "Remove Welder",
      message: `Are you sure you want to remove ${welder.badgeNumber}?`,
      onAccept: () => {
        const welders = data.welders?.filter((x) => x.welderId !== welder.welderId);
        form.setValue("welders", welders, {
          shouldDirty: true,
        });
      },
    });
  }

  function handleSelectionChanged(welder: Welder, selected: boolean) {
    if (selected) {
      setSelected((prev) => [...prev, welder]);
    } else {
      setSelected((prev) => prev.filter((x) => x.name !== welder.name));
    }
  }

  return (
    <div className="mb-5">
      <hr className="mt-4" />
      <div className="d-flex justify-content-between">
        <h3>Welders</h3>
        {permissions.data?.accessLevel !== AccessLevel.GeneralUser && (
          <div className="d-flex gap-2 my-auto">
            <IconButton icon={faSearch} title="Search" disabled={data.locked} onClick={handleSelectWelders} />
            <IconButton
              icon={faRefresh}
              title="Update Results"
              disabled={data.locked || data.welders?.length === 0}
              isLoading={update.isLoading}
              onClick={form.handleSubmit(handleRefresh)}
            />
            <IconButton
              icon={faUserPlus}
              title="Add Renewal"
              disabled={selected.length === 0}
              onClick={handleAddRenewal}
            />
            <IconButton
              icon={faCalendar}
              title="Create test event"
              disabled={selected.length === 0}
              onClick={handleCreateTestEvent}
            />
          </div>
        )}
      </div>
      <div>
        {data.welders && data.welders.length > 0 ? (
          data.welders.map((welder) => (
            <SOWFormWelderRow
              key={welder.welderId}
              scope={data}
              welder={welder}
              onRemoveWelder={handleRemoveWelder}
              onSelectionChanged={handleSelectionChanged}
              isRefreshing={update.isLoading}
            />
          ))
        ) : (
          <p>Click the search button to add welders.</p>
        )}
      </div>
    </div>
  );
}
